import { FC, memo, useMemo } from 'react';
import { TaskCenterQueries, TaskCenterTypes, TaskCenterTypesV2 } from '@frontend/api-task-center';
import { PaginatedList, EmptyStateConfig } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useContactTaskListStore, useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { contactPanelTaskListTrackingIds, taskCenterTrackingIds } from '../tracking-ids';
import { getTaskEmptyState } from '../utils';
import { prepareTaskFilters, preparePagination } from '../utils/prepare-task-filters';
import { TaskItem } from './task-item/task-item';

const EMPTY_TASK_LIST: TaskCenterTypes.Task[] = [];

interface Props {
  patientId?: string;
  statuses?: TaskCenterTypesV2.SchemaTypes.Enums.TaskStatus[];
  listQueryEnabled: boolean;
  onCreateTask: () => void;
}

export const PaginatedTaskList: FC<Props> = memo(({ statuses, patientId, listQueryEnabled, onCreateTask }) => {
  const { t } = useTranslation('task-center');
  const tokens = useTranslationTokens();
  const {
    locationIds,
    assigneeFilter,
    selectedFilters: globalSelectedFilters,
    isAIRCreatorEnabled,
  } = useTaskCenterStore(['locationIds', 'assigneeFilter', 'selectedFilters', 'isAIRCreatorEnabled']);
  const contactPanelSelectedFilters = useContactTaskListStore((s) => s.selectedFilters);

  const selectedFilters = contactPanelSelectedFilters || globalSelectedFilters;
  const isFromContactPanel = Boolean(patientId);
  const trackingIds = isFromContactPanel ? contactPanelTaskListTrackingIds : taskCenterTrackingIds;

  const filters = useMemo(
    () =>
      prepareTaskFilters({
        locationIds,
        selectedFilters,
        statuses,
        assigneeFilter: isFromContactPanel ? undefined : assigneeFilter,
        patientId: isFromContactPanel ? patientId : undefined,
        isAIRCreatorEnabled,
      }),
    [locationIds, selectedFilters, assigneeFilter, statuses, isFromContactPanel, patientId, isAIRCreatorEnabled]
  );

  const pagination = useMemo(() => preparePagination(selectedFilters), [selectedFilters]);

  const {
    data: paginatedTaskList,
    hasNextPage,
    status: queryStatus,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = TaskCenterQueries.useGetTasksListV2({ filters, pagination }, { enabled: listQueryEnabled });

  const list = useMemo(
    () => paginatedTaskList?.pages?.flatMap((page) => page.tasks).filter((task) => !!task),
    [paginatedTaskList]
  );

  const emptyStateConfig = useMemo<EmptyStateConfig>(() => {
    if (isFromContactPanel) {
      return {
        type: 'contactPanelTaskList',
        header: t(
          'No active tasks found related to this {{contact}}. Try adjusting filters or create a new task to track important actions and follow-ups.',
          {
            contact: tokens.individualContact.toLowerCase(),
          }
        ),
        styles: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
        action: {
          label: t('Create Task'),
          onClick: onCreateTask,
          trackingId: trackingIds.createTaskButton,
          'aria-label': t('Create Task'),
        },
      };
    }

    return {
      type: getTaskEmptyState(statuses?.[0]),
      ...(statuses?.[0] !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && {
        action: {
          label: t('Create Task'),
          onClick: onCreateTask,
          trackingId: trackingIds.createTaskButton,
          'aria-label': t('Create Task'),
        },
      }),
      header: t('No Tasks Found'),
      description: (
        <Text css={{ padding: theme.spacing(0, 1) }}>
          {t('Try adjusting filters, check other tabs, or create a new task to get started.')}
        </Text>
      ),
    };
  }, [isFromContactPanel, onCreateTask, statuses, t, tokens.individualContact, trackingIds.createTaskButton]);

  return (
    <PaginatedList
      listData={list ?? EMPTY_TASK_LIST}
      renderListItem={({ listItem }) => (
        <TaskItem key={listItem.id} {...listItem} trackingIds={trackingIds} isFromContactPanel={isFromContactPanel} />
      )}
      hasNextPage={hasNextPage}
      isError={queryStatus === 'error'}
      isLoading={isLoading || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={20}
      loadMore={fetchNextPage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={t('You have reached the end of the list 🎉')}
      height='100%'
    />
  );
});

PaginatedTaskList.displayName = 'PaginatedTaskList';
